import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LightBulbIcon, ArrowRightIcon, PlayCircleIcon, SparklesIcon } from '@heroicons/react/24/solid';

const Bashar15MinutesAndCounting = ({ nextActivity, onProceed }) => {
    const [meditationStep, setMeditationStep] = useState(0); // 0: intro, 1-N: steps, N+1: conclusion
    const [isMeditationActive, setIsMeditationActive] = useState(false);

    const meditationStepsContent = [
        {
            title: "Step 1: Deep Relaxation",
            text: "Close your eyes. Take a deep breath and let go. Relax your body, mind, emotions, soul, expectations, and assumptions. Here, you are free from worry and fear. If fear appears, invite it in, give it a place, knowing you will address it later.",
            icon: <PlayCircleIcon className="w-8 h-8 text-blue-400" />
        },
        {
            title: "Step 2: Visualize Your Brain",
            text: "Imagine your brain. You might see its neurological pathways as wiring, fluids, crystals, a drawing, or a pattern. However your imagination presents it is perfect, do not judge it. This is the 'scuba mask' you use to explore physical reality.",
            icon: <LightBulbIcon className="w-8 h-8 text-purple-400" />
        },
        {
            title: "Step 3: Identify and Rewire a Pathway",
            text: "Take one of those neurological pathways, one that represents a belief, thought, or feeling. In your imagination, disconnect it and reconnect it somewhere else. Then, again somewhere else. Play with this circuit freely.",
            icon: <SparklesIcon className="w-8 h-8 text-yellow-400" />
        },
        {
            title: "Step 4: Observe and Feel",
            text: "Pay close attention to how it feels each time you reconfigure a pathway. Do new sensations, ideas, inspirations, or memories arise? The differences you experience are the key. If you don't feel anything, that's okay too.",
            icon: <LightBulbIcon className="w-8 h-8 text-orange-400" />
        },
        {
            title: "Step 5: Solidify Your New Connection",
            text: "Now connect that small pathway in a final place that feels comfortable, good, cozy, warm, peaceful, loving, cool, or pleasant to you. Inhale deeply, and as you exhale, allow that neurological pathway to begin to crystallize and wire itself into this new place of perfect peace, joy, and love.",
            icon: <PlayCircleIcon className="w-8 h-8 text-green-400" />
        }
    ];

    const handleNextMeditationStep = () => {
        if (meditationStep < meditationStepsContent.length) {
            setMeditationStep(meditationStep + 1);
        } else {
            setIsMeditationActive(false); // Meditation completed
        }
    };

    const handleStartMeditation = () => {
        setIsMeditationActive(true);
        setMeditationStep(0);
    };

    const TheorySection = () => (
        <div className="bg-gray-900 p-6 rounded-xl border border-blue-500/20 mb-8">
            <h2 className="text-2xl font-bold text-blue-400 mb-4 text-center">Create Your Reality: Your Brain as an Antenna</h2>
            <div className="flex flex-col items-center gap-4 text-center">
                <div className="max-w-md">
                    <h3 className="text-xl font-bold text-white mb-2">The Higher Mind and the Physical Mind</h3>
                    <p className="text-gray-400">Your <span className="font-bold text-yellow-300">Higher Mind (Higher Self)</span> conceives and downloads energy, information, and concepts from non-physical reality. Your <span className="font-bold text-purple-300">Brain</span> acts as the receiver, the antenna, the wiring that configures this information. Your <span className="font-bold text-cyan-300">Physical Personality</span> interprets, receives, and perceives it as your physical reality.</p>
                </div>
                <ArrowRightIcon className="w-8 h-8 text-white/30 my-1 rotate-90 sm:rotate-0" />
                <div className="max-w-md">
                    <h3 className="text-xl font-bold text-white mb-2">Beliefs: The Wiring of Your Reality</h3>
                    <p className="text-gray-400">The way your brain interprets the downloads from the higher mind is determined by your <span className="font-bold text-orange-300">Belief Systems</span>. If you wish to change your experience of physical reality, you must change these belief systems that are 'wired' into your brain. Reality is not solid, only the experience of it is real, and that experience is defined by your beliefs.</p>
                </div>
            </div>
            <p className="mt-6 text-blue-200/90 italic text-center">
                "Physical reality isn't really solid, it's really only the experience of it that's real, but not physical reality itself... And the experience of physical reality is determined by the belief systems and definitions that are wired into your brain."
            </p>
        </div>
    );

    const PlasticityTheorySection = () => (
        <div className="bg-gray-900 p-6 rounded-xl border border-yellow-500/20 mb-8">
            <h2 className="text-2xl font-bold text-yellow-400 mb-4 text-center">Brain Plasticity and "15 Minutes and Counting"</h2>
            <div className="flex flex-col items-center gap-4 text-center">
                <div className="max-w-md">
                    <h3 className="text-xl font-bold text-white mb-2">Every Thought Rewires Your Brain</h3>
                    <p className="text-gray-400">Every thought, feeling, and belief you have literally <span className="font-bold text-green-300">changes the neurological wiring of your brain</span>. These pathways represent new frequencies, ideas, and beliefs that become imprinted on your brain.</p>
                </div>
                <ArrowRightIcon className="w-8 h-8 text-white/30 my-1 rotate-90 sm:rotate-0" />
                <div className="max-w-md">
                    <h3 className="text-xl font-bold text-white mb-2">The 15-Minute Solidification Cycle</h3>
                    <p className="text-gray-400">It takes approximately <span className="font-bold text-orange-300">fifteen minutes</span> from the moment a new thought, belief, or feeling begins to plasticize a neurological pathway until that pathway is fully rewired. This is the crucial time span for the solidification of new connections.</p>
                </div>
            </div>
            <p className="mt-6 text-yellow-200/90 italic text-center">
                "This transmission... is specifically designed to help rewire the brain in a way that makes it more superconductive, more hyperconductive, more receptive of the initial and original intentional vibrational frequency of the higher mind's download."
            </p>
        </div>
    );

    const CoreConcepts = () => (
        <div className="bg-gray-900 p-6 rounded-xl border border-green-500/20 mb-8">
            <h2 className="text-2xl font-bold text-green-400 mb-4 text-center">Key Concepts for Co-Creating Your Reality</h2>
            <ul className="list-disc list-inside space-y-3 text-gray-300">
                <li><span className="font-bold text-white">Follow Your Highest Joy/Passion:</span> Acting on what excites you most at any given moment is the thread leading to all other emotions and synchronicity. You don't have to understand 'how' it will happen.</li>
                <li><span className="font-bold text-white">Fear is a Messenger:</span> Pain is a product of resistance to your natural self. Fear signals a belief that is misaligned with your preferred truth. Acknowledge it, use it, and it will dissipate.</li>
                <li><span className="font-bold text-white">"Permission Slips":</span> Techniques, tools, and rituals are simply permissions to be more of who you already are. The change comes from you, not the tool.</li>
                <li><span className="font-bold text-white">Imagination: The Conduit:</span> Your imagination is the key to your frequency and the communication bridge between your higher and physical minds. Use it to explore and simulate the reality you desire.</li>
                <li><span className="font-bold text-white">Abundance is the Ability:</span> Abundance is simply the ability to do what you need to do, when you need to do it. Relax your definitions of how it must manifest.</li>
                <li><span className="font-bold text-white">You Don't Change the World, You Change Yourself:</span> By changing your vibrational frequency, you shift to an already existing parallel Earth that resonates with your new vibration.</li>
                <li><span className="font-bold text-white">All Is One (The Prime Radiant Particle):</span> In essence, only one particle exists in all of existence, moving at infinite speed and being everywhere at once. This means everything is made of the same thing, and you are literally everything.</li>
            </ul>
        </div>
    );

    const MeditationSection = () => (
        <div className="bg-gray-900 p-6 rounded-xl border border-purple-500/20 mb-8 text-center">
            <h2 className="text-2xl font-bold text-purple-400 mb-4">Neural Rewiring Meditation</h2>
            {!isMeditationActive ? (
                <>
                    <p className="text-gray-300 mb-4">This guided meditation will help you experience the plasticity of your brain and begin the process of consciously rewiring your neurological pathways, aligning them with your highest desires.</p>
                    <button
                        onClick={handleStartMeditation}
                        className="inline-flex items-center gap-2 bg-purple-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-purple-500 transition-colors"
                    >
                        <PlayCircleIcon className="w-6 h-6"/>
                        Start Meditation
                    </button>
                </>
            ) : (
                <div className="min-h-[300px] flex flex-col justify-between">
                    {meditationStep < meditationStepsContent.length ? (
                        <div className="animate-fade-in">
                            <div className="mb-4 flex justify-center">{meditationStepsContent[meditationStep].icon}</div>
                            <h3 className="text-xl font-bold text-white mb-2">{meditationStepsContent[meditationStep].title}</h3>
                            <p className="text-gray-300 mb-6">{meditationStepsContent[meditationStep].text}</p>
                            <button
                                onClick={handleNextMeditationStep}
                                className="inline-flex items-center gap-2 bg-purple-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-purple-500 transition-colors"
                            >
                                {meditationStep < meditationStepsContent.length - 1 ? "Next Step" : "Finish Meditation"}
                                <ArrowRightIcon className="w-5 h-5"/>
                            </button>
                            <p className="text-gray-500 text-sm mt-4">Step {meditationStep + 1} of {meditationStepsContent.length}</p>
                        </div>
                    ) : (
                        <div className="animate-fade-in">
                            <SparklesIcon className="w-12 h-12 text-green-400 mx-auto mb-3"/>
                            <h3 className="2xl font-bold text-green-300 mb-2">Meditation Complete: Rewiring in Progress!</h3>
                            <p className="text-gray-300 mb-4">You have initiated a subtle but profound process of neural rewiring. The new connections will solidify over the next 15 minutes. Continue to act on your highest joy and watch as your reality aligns with your truth.</p>
                            <button
                                onClick={() => setIsMeditationActive(false)}
                                className="inline-flex items-center gap-2 bg-gray-700 text-gray-300 font-bold py-2 px-6 rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                Understood
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div className="w-full bg-gray-900 animate-fade-in"> {/* Outer container for full-width dark background */}
            <div className="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8"> {/* Inner container for centered content */}
                <TheorySection />
                <PlasticityTheorySection />
                <CoreConcepts />
                <MeditationSection />

                <div className="mt-12 text-center">
                    <div className="bg-gray-900 p-6 rounded-xl border border-teal-500/20 mb-8 max-w-2xl mx-auto">
                         <LightBulbIcon className="w-10 h-10 text-teal-400 mx-auto mb-3" />
                        <h2 className="text-xl font-bold text-teal-300 mb-2">Your Power of Creation</h2>
                        <p className="text-gray-300">
                            By understanding your brain's plasticity and the role of your beliefs, you hold the power to rewire your reality. Always act from your greatest excitement and watch how the universe synchronistically supports you.
                        </p>
                    </div>
                    {nextActivity && (
                        <button
                            onClick={onProceed}
                            className="inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-colors mb-4"
                        >
                            Continue to the Next Activity
                            <ArrowRightIcon className="w-5 h-5"/>
                        </button>
                    )}
                    <div className="flex flex-col items-center gap-4 mt-6">
                        <Link to="/bashar/tools/15-min-count/2"
                              className="inline-flex items-center gap-2 bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-500 transition-colors">
                            Go to Part 2
                            <ArrowRightIcon className="w-5 h-5"/>
                        </Link>
                        <Link to="/courses/manifest" className="text-sm text-gray-500 hover:text-yellow-400 transition-colors">Back to Syllabus</Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Bashar15MinutesAndCounting;